import React from 'react';
import { cn } from '../../lib/utils';

export interface MetricItemProps {
  label: string;
  value: string | number;
  detail?: string;
  className?: string;
}

export function MetricItem({ label, value, detail, className }: MetricItemProps) {
  return (
    <div className={cn('flex flex-col gap-0.5 px-3 py-1.5 sm:gap-1 sm:px-4 sm:py-2 3xl:px-8 3xl:py-4 3xl:gap-2', className)}>
      <span className="text-[9px] sm:text-[10px] 3xl:text-sm font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </span>
      <div className="flex items-baseline gap-1.5 sm:gap-2 3xl:gap-4">
        <span className="text-lg sm:text-xl 3xl:text-4xl 4k:text-5xl font-bold tracking-tight text-foreground font-heading leading-tight">
          {value}
        </span>
        {detail && (
          <span className="text-[10px] sm:text-xs 3xl:text-base text-muted-foreground truncate font-medium">
            {detail}
          </span>
        )}
      </div>
    </div>
  );
}

export interface MetricBarProps {
  items?: MetricItemProps[];
  children?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'flat';
}

export function MetricBar({ items, children, className, variant = 'default' }: MetricBarProps) {
  const baseStyles = variant === 'default' 
    ? 'rounded-lg border border-border/50 bg-muted/20'
    : 'border-b border-border/40 bg-transparent rounded-none';

  return (
    <div
      className={cn(
        'grid grid-cols-2 md:flex md:flex-wrap items-center divide-x divide-y md:divide-y-0 divide-border/40',
        baseStyles,
        className
      )}
    >
      {items ? (
        items.map((item, idx) => (
          <MetricItem 
            key={idx} 
            {...item} 
            className={cn(
              item.className,
              // Fix border logic for 2-column grid
              idx % 2 === 0 ? 'border-l-0' : 'border-l-0 md:border-l',
              idx < 2 ? 'border-t-0' : 'border-t-0 md:border-t-0'
            )} 
          />
        ))
      ) : children}
    </div>
  );
}
